import React from "react";
import { InfoOutlined } from "@mui/icons-material";
import {
  Badge,
  Box,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Link } from "react-router-dom";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { useTablePagination } from "hooks";
import { useContractorDetailContext } from "contexts/ContractorDetailContext";
import { useLazyListContractorShipmentsQuery } from "store/services/contractorApi";

export const ContractorShipmentList = () => {
  const { contractorDetail } = useContractorDetailContext();
  
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [statusFilter, setStatusFilter] = React.useState("All");

  const statusOptions = ["All", "Pending Delivery", "Delivered"];

  const columns = [
    {
      name: "ID",
      selector: (row) => row?.shipmentId,
      cell: (row) => row?.shipmentId,
      width: "150px",
    },
    {
      name: "Sender",
      selector: (row) =>
        row?.customerUser?.name || row?.vendor?.ownerName || "N/A",
      cell: (row) => (
        <Box
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row?.customerUser?.name || row?.vendor?.ownerName || "N/A"}
        </Box>
      ),
    },
    {
      name: "Receiver",
      selector: (row) => row?.receiver?.customerUser?.name,
      cell: (row) => (
        <div>
          <span className="text-sm">{row?.receiver?.customerUser?.name}</span>
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-300">
            {row?.receiver?.phone}
          </span>
        </div>
      ),
    },
    {
      name: "From",
      selector: (row) => row?.sourceBranch?.name || "Loading...",
      cell: (row) => (
        <span>{row?.sourceBranch?.name || <CircularProgress size={12} />}</span>
      ),
    },
    {
      name: "To",
      selector: (row) => row?.destinationBranch?.name || "Loading...",
      cell: (row) => (
        <span>
          {row?.destinationBranch?.name || <CircularProgress size={12} />}
        </span>
      ),
    },
    {
      name: "Created At",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      center: true,
      cell: (row) => {
        const statusColors = {
          SendOut: "bg-blue-700 text-white",
          Delivered: "bg-green-700 text-white",
        };
        return (
          <Badge
            className={`rounded-md px-1 py-1 ${
              statusColors[row.status] || "bg-gray-500 text-white"
            }`}
          >
            {row.status}
          </Badge>
        );
      },
    },
    {
      name: "Actions",
      cell: (row) => {
        const basePath = "/app/crm/shipment";
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
          >
            <Link to={`${basePath}/${row?._id}/detail`}>
              <IconButton
                size="small"
                aria-label="View Shipment"
                color="primary"
              >
                <InfoOutlined />
              </IconButton>
            </Link>
          </Box>
        );
      },
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [fetchShipments, { data: shipmentRes, isLoading, isFetching }] =
    useLazyListContractorShipmentsQuery();

  React.useEffect(() => {
    if (!contractorDetail?._id) return;

    const params = {
      contractorId: contractorDetail?._id,
      page: currentPage,
      limit: perPage,
    };

    if (statusFilter !== "All") {
      params.status = statusFilter === "Pending Delivery" ? "SendOut" : "Delivered";
    }

    fetchShipments(params);
  }, [
    currentPage,
    contractorDetail?._id,
    fetchShipments,
    perPage,
    statusFilter,
  ]);

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={1} mt={1}>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(e.target.value);
              handlePageChange(1); // Reset to first page on filter change
            }}
            size="small"
          >
            {statusOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <AppTable
        columns={columns}
        data={shipmentRes?.data || []}
        progressPending={isLoading || isFetching}
        paginationServer
        paginationPerPage={perPage}
        paginationTotalRows={shipmentRes?.totalItems || 0}
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
      />
    </>
  );
};